import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState(null)

  useEffect(() => {
    let activo = true

    const cargarSesion = async () => {
      const { data, error } = await supabase.auth.getSession()

      if (!activo) return

      if (error) {
        console.error('Error al recuperar la sesión:', error)
        setAuthError(error.message)
      }

      const currentSession = data?.session ?? null

      setSession(currentSession)
      setUser(currentSession?.user ?? null)
      setLoading(false)
    }

    cargarSesion()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nuevaSession) => {
      if (!activo) return

      if (event === 'SIGNED_OUT') {
        setSession(null)
        setUser(null)
        setAuthError(null)
        setLoading(false)
        return
      }

      if (event === 'TOKEN_REFRESHED') {
        // Supabase renueva el access token en segundo plano (por ejemplo
        // al recuperar el foco de la pestaña). La sesión se actualiza,
        // pero si el usuario es el mismo se conserva la referencia de
        // `user` para no disparar efectos que dependan de su identidad
        // (como la recarga de empresas) y terminen desmontando la app.
        setSession(nuevaSession)
        setUser((usuarioActual) => {
          const nuevoUsuario = nuevaSession?.user ?? null

          return usuarioActual?.id === nuevoUsuario?.id
            ? usuarioActual
            : nuevoUsuario
        })
        return
      }

      // INITIAL_SESSION, SIGNED_IN, USER_UPDATED, etc.
      setSession(nuevaSession)
      setUser(nuevaSession?.user ?? null)
      setAuthError(null)
      setLoading(false)
    })

    return () => {
      activo = false
      subscription.unsubscribe()
    }
  }, [])

  const cerrarSesion = async () => {
    const { error } = await supabase.auth.signOut()

    if (error) {
      throw error
    }
  }

  const value = useMemo(
    () => ({
      session,
      user,
      loading,
      authError,
      cerrarSesion,
    }),
    [session, user, loading, authError]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error(
      'useAuth debe utilizarse dentro de AuthProvider'
    )
  }

  return context
}